import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Navbar from "../Navbar";
import Footer from "../Footer";
import Sidebar from "../Sidebar";
import IP_URL from "../connect";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { useSelector } from "react-redux";
import { selectUser } from "../../Redux/currentUserSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaMapMarkerAlt, FaSearch } from "react-icons/fa";
import { useParams } from "react-router-dom";

// ---------- Styled Components ----------
const Main = styled.div`
  background: #f6f8fa;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  flex: 1;
  padding: 1rem;
  display: flex;
  justify-content: center;
  margin-bottom: 50px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  padding: 1rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  text-align: center;

  h2 {
    color: #2563eb;
    margin-bottom: 4px;
  }

  p {
    font-size: 14px;
    color: #6b7280;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  label {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 5px;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 14px;
  outline: none;
  transition: border 0.2s ease;
  &:focus {
    border-color: #2563eb;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 14px;
  outline: none;
  background: white;
  &:focus {
    border-color: #2563eb;
  }
`;

const MapSection = styled.div`
  h4 {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #374151;
    margin-bottom: 10px;
  }
  position: relative;
`;

const SearchBar = styled.form`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  display: flex;
  align-items: center;
  width: calc(100% - 20px);
  max-width: 350px;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;

  input {
    flex: 1;
    padding: 10px;
    border: none;
    outline: none;
  }

  button {
    background: #2563eb;
    color: white;
    border: none;
    padding: 10px 12px;
    cursor: pointer;
  }
`;

const AddBtn = styled.button`
  color: white;
  width: 100%;
  padding: 14px;
  background-color: ${(props) => (props.disabled ? "#9ca3af" : "#22c55e")};
  border: none;
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  font-size: 15px;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) => (props.disabled ? "#9ca3af" : "#16a34a")};
  }
`;

// ---------- Leaflet Icon Fix ----------
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// ---------- Location Marker ----------
const LocationMarker = ({ selectedLocation, setSelectedLocation, setMeterAddress }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setSelectedLocation({ lat, lng });
      setMeterAddress({ lat, lng });
    },
  });

  return selectedLocation ? (
    <Marker
      position={[selectedLocation.lat, selectedLocation.lng]}
      draggable
      eventHandlers={{
        dragend: (e) => {
          const { lat, lng } = e.target.getLatLng();
          setSelectedLocation({ lat, lng });
          setMeterAddress({ lat, lng });
        },
      }}
    />
  ) : null;
};

// ---------- Search Control ----------
const SearchControl = ({ onSearch }) => {
  const [query, setQuery] = useState("");
  const map = useMap();

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return toast.warning("Please enter a location");

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
      );
      const data = await res.json();

      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const location = { lat: parseFloat(lat), lng: parseFloat(lon) };
        map.flyTo(location, 15);
        onSearch(location);
        toast.success("Location found!");
      } else toast.error("No results found");
    } catch {
      toast.error("Error searching location");
    }
  };

  return (
    <SearchBar onSubmit={handleSearch}>
      <input
        type="text"
        placeholder="Search for a location..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <button type="submit">
        <FaSearch />
      </button>
    </SearchBar>
  );
};

// ---------- Main Component ----------
const AddMeter = () => {
  const [meterId, setMeterId] = useState("");
  const [meterName, setMeterName] = useState("");
  const [meterType, setMeterType] = useState("");
  const [meterSize, setMeterSize] = useState("");
  const [meterAddress, setMeterAddress] = useState({});
  const [serialNumber, setSerialNumber] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { token } = useSelector(selectUser);
  const {uniqueKey} = useParams()

  const sizeOptions = ["DN15", "DN20", "DN25", "DN32", "DN40", "DN50", "DN65", "DN80", "DN100", "DN150", "DN200", "DN300"];
  const typeOptions = ["Borehole", "Prepaid", "Vavle Control", "Non Valve ", "Abstraction"]
  // Default location = user's current position
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        const { latitude, longitude } = pos.coords;
        const location = { lat: latitude, lng: longitude };
        setSelectedLocation(location);
        setMeterAddress(location);
      });
    }
  }, []);

  const allFieldsFilled =
    meterId && serialNumber && meterName && meterType && meterSize && meterAddress?.lat && meterAddress?.lng;

  const addMeter = async () => {
    //if (!allFieldsFilled) return toast.warning("Please fill all fields and select a location.");
    setSubmitting(true);
    try {
      const res = await fetch(`${IP_URL}/organization/${uniqueKey}/add-meter`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ meterId, meterName, meterType, meterSize, meterAddress, serialNumber }),
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.message || "Error adding meter");
      else {
        toast.success("Meter added successfully!");
        setTimeout(() => navigate(`/admin-meter-details/${meterId}`), 1500);
        setMeterId("");
        setMeterName("");
        setMeterType("");
        setMeterSize("")
        setSerialNumber("");
      }
    } catch {
      toast.error("Network error, please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Main>
      <Navbar showMenuButton toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

      <Container>
        <Card>
          <Header>
            <h2>Add New Meter</h2>
            <p>Register and map a new meter to organization</p>
          </Header>

          <FormGrid>
            <FormGroup>
              <label>Meter IMEI</label>
              <Input value={meterId} onChange={(e) => setMeterId(e.target.value)} />
            </FormGroup>

            <FormGroup>
              <label>Serial Number</label>
              <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
            </FormGroup>

            <FormGroup>
              <label>Meter Name</label>
              <Input value={meterName} onChange={(e) => setMeterName(e.target.value)} />
            </FormGroup>
  <FormGroup>
              <label>Meter Type</label>
              <Select value={meterType} onChange={(e) => setMeterType(e.target.value)}>
                <option value="">Select Meter Type</option>
                {typeOptions.map((opt, i) => (
                  <option key={i} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <label>Meter Size</label>
              <Select value={meterSize} onChange={(e) => setMeterSize(e.target.value)}>
                <option value="">Select Meter Size</option>
                {sizeOptions.map((opt, i) => (
                  <option key={i} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
            </FormGroup>
            
          </FormGrid>

          <MapSection>
            <h4>
              <FaMapMarkerAlt /> Select Meter Location
            </h4>
            <MapContainer
              center={selectedLocation || { lat: -1.2921, lng: 36.8219 }}
              zoom={15}
              style={{ height: "350px", width: "100%", borderRadius: "10px" }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <SearchControl
                onSearch={(loc) => {
                  setSelectedLocation(loc);
                  setMeterAddress(loc);
                }}
              />
              <LocationMarker
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                setMeterAddress={setMeterAddress}
              />
            </MapContainer>
          </MapSection>

          <AddBtn onClick={addMeter} disabled={!allFieldsFilled  || submitting}>
            <AddIcon /> {submitting ? "Adding..." : "Add Meter"}
          </AddBtn>
        </Card>
      </Container>

      <Footer />
      
    </Main>
  );
};

export default AddMeter;
